//-- Copyright (c) 2021 TMT Insights Group, LLC
import { createRef, forwardRef, RefObject, useContext, useEffect, useImperativeHandle } from 'react';
import styled from '@emotion/styled';
import { Box, Fade, Grid, Typography, Chip } from '@mui/material';
import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { TitleEntity } from '@/redux/slices/slice_types';
import { getTitleDetails, setActiveTitle, setFilteredChildId } from '@/redux/slices/titleSlice';
import { clearCache, fetchTitleChildrenV2, fetchTitleV2, getTitleVersions, setActiveTitleId, setRootTitleId } from '@/redux/slices/titleV2Slice';
import { clearInventoryResults, setSearchValue } from '@/redux/slices/orderPlacementSlice';
import { selectTitleSearchResults } from '@/redux/selectors/orderPlacementSelectors';
import { convertStylesToStyled, insertWordBreaks } from '@/_utils/utils';
import { useToast } from '@/components/_common/Toast';
import Thumbnail from '@/components/_common/Thumbnail';
import TitleTypeLabel from '@/components/_common/TitleTypeLabel';

type OPTitleSearchResultsProps = {
  show: boolean;
  isSearching: boolean;
  clearInput: () => void;
  searchValue: string;
  focusIndex: null | number;
};

const OPTitleSearchResults = ({ show, isSearching, clearInput, searchValue, focusIndex }: OPTitleSearchResultsProps, ref: any) => {
  const { error: errorToast } = useToast();
  const isTitleV2 = useContext(ConfigContext)?.titleConfig?.v2;
  const titleSearchResult = useContext(ConfigContext)?.orderPlacementConfig?.titleSearchResult;
  const dispatch = useAppDispatch();
  const results = useAppSelector(selectTitleSearchResults).map((result) => ({ ...result, ref: createRef() }));
  const contextualSelectEntityIds = useContext(ConfigContext)?.orderPlacementConfig?.contextualSelectEntityIds || [];

  const onTitleClick = (result: TitleEntity) => {
    setTimeout(() => {
      if (isTitleV2) {
        dispatch(setActiveTitleId(null));
      } else {
        dispatch(setActiveTitle(null));
        dispatch(setFilteredChildId(null));
      }
      dispatch(clearInventoryResults(undefined));
      dispatch(setSearchValue(searchValue));
      clearInput();

      if (isTitleV2) {
        dispatch(clearCache());
        dispatch(setRootTitleId(result.id));
        dispatch(setActiveTitleId(result.id));
        dispatch(fetchTitleV2(result.id))
          .unwrap()
          .then((titleData) => {
            if (titleData.relationships.children.every((child) => child.entity_type === 'version')) {
              dispatch(getTitleVersions(titleData.id));
            }
          })
          .catch((err) => errorToast(err.message || 'There was a problem loading the title.'));
        dispatch(fetchTitleChildrenV2(result.id));
      } else {
        const isEntityTypeInContextualList = contextualSelectEntityIds?.length && contextualSelectEntityIds.includes(result.entity_type || '');
        const shouldFilter = isEntityTypeInContextualList && result.parent;
        const idToFetch = shouldFilter ? result.parent! : result.id;

        if (shouldFilter) {
          dispatch(setFilteredChildId(result.id));
        } else {
          dispatch(setFilteredChildId(null));
        }

        dispatch(getTitleDetails(idToFetch))
          .unwrap()
          .then(() => dispatch(setActiveTitle(idToFetch)))
          .catch((err) => errorToast(err.message || 'There was a problem loading the title.'));
      }
    }, 200);
  };

  useEffect(() => {
    if (focusIndex !== null && results[focusIndex]?.ref?.current) {
      (results[focusIndex].ref as RefObject<HTMLElement>).current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }
  }, [focusIndex]);

  useImperativeHandle(ref, () => ({ onTitleClick }));

  return (
    <Fade ref={ref} in={show} timeout={500}>
      <SearchResults>
        <ResultsList isSearching={isSearching}>
          {results.length ? (
            results.map((result: TitleEntity, index: number) => {
              const { id, entity_type, release_year, thumbnail_link, ref } = result;
              const releaseYear = release_year ? new Date(release_year).getFullYear() : 0;
              return (
                <ResultItem
                  key={id}
                  ref={ref}
                  isFocused={focusIndex === index}
                  id={`${id}`}
                  type={(entity_type || '').toLowerCase()}
                  onClick={() => onTitleClick(result)}
                >
                  {titleSearchResult ? (
                    titleSearchResult(result)
                  ) : (
                    <Grid container>
                      <Grid item display="flex" alignItems="center">
                        <Thumbnail src={thumbnail_link} />
                        <Box sx={{ ml: 1.5 }}>
                          <div>
                            <Typography sx={{ fontWeight: 'bold', fontSize: '1.1rem', mb: 0.5 }}>{insertWordBreaks(result.name)}</Typography>
                            <Grid container alignItems="center" sx={{ opacity: 0.6 }}>
                              <Typography>{releaseYear > 0 ? releaseYear : ''}</Typography>
                            </Grid>
                          </div>
                        </Box>
                      </Grid>
                      <Grid item ml="auto" alignSelf="center" columnGap={1} display="flex">
                        {result?.lineage_meta?.compilation_name && <CompilationLabel label="Compilation" />}
                        <TitleTypeLabel type={entity_type} sx={{ ml: 'auto' }} width="auto" />
                      </Grid>
                    </Grid>
                  )}
                </ResultItem>
              );
            })
          ) : (
            <ResultItem>
              <span>No results found for </span>
              <strong style={{ paddingLeft: 5 }}>&quot;{searchValue}&quot;</strong>
            </ResultItem>
          )}
        </ResultsList>
      </SearchResults>
    </Fade>
  );
};

export default forwardRef(OPTitleSearchResults);

const SearchResults = styled.div(({ theme }) => ({
  position: 'absolute',
  borderRadius: theme.shape.borderRadius,
  marginTop: theme.spacing(1),
  border: `1px solid ${theme.palette.primary.main}` + '9a',
  background: theme.palette.background.paper,
  overflow: 'auto',
  width: '100%',
  maxHeight: 350
}));

const ResultsList = styled.ul<{ isSearching: boolean }>(({ isSearching, theme }) => ({
  listStyleType: 'none',
  paddingLeft: 0,
  margin: theme.spacing(1, 0),
  opacity: isSearching ? 0.2 : 1,
  pointerEvents: isSearching ? 'none' : 'auto'
}));

const ResultItem = styled.li<{ type?: string; isFocused?: boolean }>(({ type = '', isFocused = false, theme }) => ({
  padding: theme.spacing(1, 2),
  minWidth: '25rem',
  overflow: 'hidden',
  fontSize: '1rem',
  cursor: 'pointer',
  transition: 'background-color 0.3s ease-in-out',
  color: type === 'typeicon' ? '#fff' : 'inherit',
  backgroundColor: type === 'typeicon' ? theme.palette.secondary.main : isFocused ? theme.palette.action.hover : 'transparent',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transition: 'background-color 0.3s ease-in-out'
  },
  '& strong': {
    wordBreak: 'break-word'
  },
  '& > div': {
    display: 'inline-flex',
    flexWrap: 'nowrap',
    alignItems: 'center'
  },
  '& .MuiChip-root': {
    marginRight: 0
  }
}));

const CompilationLabel = styled(Chip)`
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  margin-right: ${({ theme }) => theme.spacing(1.75)};
  text-transform: uppercase;
  border: 1px solid ${({ theme }) => theme.palette.primary.main};
  font-size: 0.8rem;
  padding: 4px;
  ${({ theme }) => convertStylesToStyled(theme.customOverrides?.TypeLabel?.root)}

  background: transparent;
  color: ${({ theme }) => theme.palette.warning.main};
  border: 1px solid ${({ theme }) => theme.palette.warning.main};
  opacity: 0.7;
`;
